import { useCallback, useRef } from 'react'
import { dataLayer } from 'analytics'
import cookieStorage from 'cookie-storage'
import localStorage from 'local-storage'
import { constants } from 'helpers'
import { useSubscription } from 'modules/subscription'


const cookiesToDelete: string[] = [
  constants.cookieNames.discountCoupon,
  constants.cookieNames.appliedCouponCode,
  constants.cookieNames.isFreeTrialCoupon,
  constants.cookieNames.country,
  constants.cookieNames.utmMediumBeforeSubscription,
  constants.cookieNames.utmSourceBeforeSubscription,
  constants.cookieNames.flowName,
  constants.cookieNames.synergyParams,
  constants.cookieNames.tophatterParams,
  constants.cookieNames.rootCountdown,
  constants.cookieNames.freePerfumeCountdown,
  constants.cookieNames.selectedPlan,
]

const localStorageToDelete: string[] = [
  constants.localStorageNames.shippingFormValues,
  constants.localStorageNames.authGender,
  constants.localStorageNames.authEmail,
  constants.localStorageNames.lastAddedToCartProductMetadata,
]

type UseProcessAnalyticsParams = {
  purchaseResult: PaymentModule.PurchaseSession
}

// put all after-purchase events her
export const useProcessAnalytics = () => {
  const { subscription } = useSubscription()
  const hasNeverSubscribedRef = useRef(subscription.hasNeverSubscribed)

  return useCallback((params: UseProcessAnalyticsParams) => {
    const { purchaseResult } = params

    const {
      hasEcommerce,
      hasSubscription,
      total,
      subscription,
      coupon,
    } = modifyPurchaseResult(purchaseResult)

    const isResubscribe = !hasNeverSubscribedRef.current && hasSubscription

    if (isResubscribe) {
      dataLayer.track('resubscribe', {
        resubscribePath: 'from payment page',
      })
    }

    const tophatterParams = cookieStorage.getItem(constants.cookieNames.tophatterParams) || {}
    const synergyParams = cookieStorage.getItem(constants.cookieNames.synergyParams) || {}

    if (subscription) {
      dataLayer.track('subscription thank you page', {
        subscription: {
          chargeAmount: total,
          coupon: coupon?.code || null,
          couponType: coupon?.type || null,
          ...subscription,
        },
        tophatterParams,
        synergyParams,
      })
    }

    if (hasEcommerce) {
      dataLayer.track('ecommerce thank you page', {
        total,
      })
    }

    localStorageToDelete.forEach((name) => localStorage.removeItem(name))

    setTimeout(() => {
      cookiesToDelete.forEach((name) => cookieStorage.removeItem(name))
    }, 10000)
  }, [])
}

const modifyPurchaseResult = (purchaseResult: PaymentModule.PurchaseSession) => {
  const { total, orders, discounts } = purchaseResult.data

  let hasEcommerce = false
  let hasSubscription = false

  let subscription: {
    id: number
    plan: string
    isGift: boolean
  } = null

  orders.forEach(({ items }) => {
    items.forEach((item) => {
      if (item.__typename === 'SubscriptionPurchaseItem') {
        subscription = {
          id: item.id,
          plan: item.plan.name,
          isGift: false,
        }

        hasSubscription = true
      }
      else if (item.__typename === 'GiftSubscriptionPurchaseItem') {
        if (!subscription) {
          subscription = {
            id: item.giftSubscriptionId,
            plan: 'MONTHLY',
            isGift: true,
          }
        }
      }
      else if (item.__typename === 'ProductPurchaseItem') {
        hasEcommerce = true
      }
    })
  })

  let coupon: {
    code: string
    type: string
  }

  discounts.some((item) => {
    if (item.coupon) {
      coupon = item.coupon
      return true
    }

    return false
  })

  return {
    total: total / 100,
    hasEcommerce,
    hasSubscription,
    subscription,
    coupon,
  }
}
